import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BugAntIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { checkProductFeedbackAccess } from '../../services/productFeedbackService';
import {
  getProductFeedbackDiagnosticRecorder,
  type ProductFeedbackDiagnosticsHealthCheckResult,
} from '../../services/productFeedbackDiagnostics';
import { canLocallyUseProductFeedbackReporter, isActiveAdmin } from '../../utils/accessControl';
import {
  captureProductFeedbackRuntime,
  isProductFeedbackEligiblePath,
  isProductFeedbackMapHost,
  isProductFeedbackRequestComposerHost,
} from '../../utils/productFeedbackRuntime';
import type {
  ProductFeedbackDiagnosticsPayload,
  ProductFeedbackRuntimeSnapshot,
} from '../../types/productFeedback';
import { ProductFeedbackReporter } from './ProductFeedbackReporter';

interface ProductFeedbackRuntimeProviderProps {
  children: React.ReactNode;
}

export type ProductFeedbackCapabilityStatus = 'checking' | 'allowed' | 'denied' | 'error';

interface ProductFeedbackCapabilityContextValue {
  status: ProductFeedbackCapabilityStatus;
  isAllowed: boolean;
  isOpening: boolean;
  openReporter: (openerElement?: HTMLElement | null) => Promise<void>;
}

const ProductFeedbackCapabilityContext = createContext<ProductFeedbackCapabilityContextValue>({
  status: 'checking',
  isAllowed: false,
  isOpening: false,
  openReporter: async () => undefined,
});

export const useProductFeedbackCapability = (): ProductFeedbackCapabilityContextValue =>
  useContext(ProductFeedbackCapabilityContext);

const isAdminPath = (pathname: string): boolean =>
  pathname === '/admin' || pathname.startsWith('/admin/');

const isEditableElement = (element: Element | null): boolean =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  element instanceof HTMLSelectElement ||
  (element instanceof HTMLElement && element.isContentEditable);

const useCompactEditableFocus = (maxWidth: 639 | 767): boolean => {
  const [hiddenForEditing, setHiddenForEditing] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const update = () =>
      setHiddenForEditing(media.matches && isEditableElement(document.activeElement));
    const handleFocus = () => update();
    media.addEventListener?.('change', update);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleFocus);
    update();
    return () => {
      media.removeEventListener?.('change', update);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleFocus);
    };
  }, [maxWidth]);

  return hiddenForEditing;
};

export const ProductFeedbackRuntimeProvider: React.FC<ProductFeedbackRuntimeProviderProps> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, claims } = useAuth();
  const recorder = getProductFeedbackDiagnosticRecorder();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const generationRef = useRef(0);
  const [capabilityStatus, setCapabilityStatus] =
    useState<ProductFeedbackCapabilityStatus>('checking');
  const [opening, setOpening] = useState(false);
  const [diagnostics, setDiagnostics] = useState<ProductFeedbackDiagnosticsPayload | null>(null);
  const [runtime, setRuntime] = useState<ProductFeedbackRuntimeSnapshot | null>(null);
  const routeEligible = isProductFeedbackEligiblePath(pathname);
  const adminPath = isAdminPath(pathname);
  const accessScope = adminPath ? 'admin' : 'reporter';
  const requestComposerHost = isProductFeedbackRequestComposerHost(pathname);
  const hiddenForEditing = useCompactEditableFocus(requestComposerHost ? 767 : 639);
  const adminRouteEligible = !adminPath || isActiveAdmin(userProfile, claims);
  const locallyEligible = Boolean(
    currentUser &&
      routeEligible &&
      adminRouteEligible &&
      canLocallyUseProductFeedbackReporter(userProfile, claims)
  );
  const reporterOpen = Boolean(diagnostics && runtime && currentUser);

  const deactivate = useCallback(
    (status: ProductFeedbackCapabilityStatus = 'denied') => {
      setCapabilityStatus(status);
      setOpening(false);
      setDiagnostics(null);
      setRuntime(null);
      recorder.stop();
      recorder.clear();
    },
    [recorder]
  );

  useEffect(() => {
    const generation = ++generationRef.current;
    if (!locallyEligible) {
      deactivate();
      return;
    }
    setCapabilityStatus('checking');

    const refreshCapability = async () => {
      try {
        const allowed = await checkProductFeedbackAccess(accessScope);
        if (generationRef.current !== generation) return;
        if (allowed !== true) {
          deactivate();
          return;
        }
        recorder.start();
        setCapabilityStatus('allowed');
      } catch {
        if (generationRef.current === generation) deactivate('error');
      }
    };

    void refreshCapability();
    const intervalId = window.setInterval(refreshCapability, 60000);
    const handleFocus = () => void refreshCapability();
    const handleVisibility = () => {
      if (!document.hidden) void refreshCapability();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      generationRef.current += 1;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      deactivate();
    };
  }, [accessScope, currentUser?.uid, deactivate, locallyEligible, recorder]);

  const openReporter = useCallback(
    async (openerElement?: HTMLElement | null) => {
      if (!locallyEligible || opening) return;
      openerRef.current = openerElement || triggerRef.current;
      setOpening(true);
      const generation = generationRef.current;
      try {
        const allowed = await checkProductFeedbackAccess(accessScope);
        if (generationRef.current !== generation || !locallyEligible) return;
        if (allowed !== true) {
          deactivate();
          return;
        }
        recorder.start();
        setCapabilityStatus('allowed');
        setDiagnostics(recorder.getSnapshot({ included: true }));
        setRuntime(captureProductFeedbackRuntime(pathname));
      } catch {
        if (generationRef.current === generation) deactivate('error');
      } finally {
        if (generationRef.current === generation) setOpening(false);
      }
    },
    [accessScope, deactivate, locallyEligible, opening, pathname, recorder]
  );

  const closeReporter = () => {
    setDiagnostics(null);
    setRuntime(null);
  };

  const refreshReporterSnapshot = useCallback(() => {
    setDiagnostics(recorder.getSnapshot({ included: true }));
    setRuntime(captureProductFeedbackRuntime(pathname));
  }, [pathname, recorder]);

  const checkDiagnosticsHealth = useCallback(
    (): ProductFeedbackDiagnosticsHealthCheckResult => recorder.checkHealth(),
    [recorder]
  );

  const offsetClass = pathname.startsWith('/admin')
    ? 'bottom-[calc(env(safe-area-inset-bottom)+5rem)] md:bottom-4'
    : isProductFeedbackMapHost(pathname)
      ? 'bottom-[calc(env(safe-area-inset-bottom)+5rem)] md:bottom-20'
      : requestComposerHost
        ? 'bottom-[calc(env(safe-area-inset-bottom)+5rem)] md:bottom-4'
        : 'bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.75rem))]';
  const triggerHidden = reporterOpen || hiddenForEditing;

  const capabilityValue = useMemo(
    () => ({
      status: capabilityStatus,
      isAllowed: capabilityStatus === 'allowed',
      isOpening: opening,
      openReporter,
    }),
    [capabilityStatus, openReporter, opening]
  );

  const reporterHost = pathname === '/app/product-feedback';

  return (
    <ProductFeedbackCapabilityContext.Provider value={capabilityValue}>
      {children}
      {capabilityStatus === 'allowed' && !reporterHost && (
        <button
          ref={triggerRef}
          type="button"
          onClick={(event) => void openReporter(event.currentTarget)}
          disabled={opening || triggerHidden}
          aria-label="Zgłoś problem lub sugestię"
          aria-hidden={triggerHidden ? true : undefined}
          tabIndex={triggerHidden ? -1 : undefined}
          className={`product-feedback-trigger fixed right-[max(1rem,calc(env(safe-area-inset-right)+0.75rem))] z-[45] flex h-11 w-11 items-center justify-center rounded-full border border-primary-dark-300/70 bg-primary-dark-200/95 text-primary-light-300 shadow-card transition hover:border-ratuj-300/55 hover:text-ratuj-300 focus-outline disabled:cursor-wait disabled:opacity-60 ${triggerHidden ? 'invisible pointer-events-none' : ''} ${offsetClass}`}
        >
          <BugAntIcon
            className="h-5 w-5"
            aria-hidden="true"
            data-testid="product-feedback-bug-icon"
          />
        </button>
      )}
      {reporterOpen && diagnostics && runtime && currentUser && (
        <ProductFeedbackReporter
          diagnostics={diagnostics}
          runtime={runtime}
          reporterUid={currentUser.uid}
          triggerRef={openerRef}
          onRefreshSnapshot={refreshReporterSnapshot}
          onCheckDiagnostics={checkDiagnosticsHealth}
          onViewReport={(reportId) => {
            closeReporter();
            navigate(`/app/product-feedback?reportId=${encodeURIComponent(reportId)}`);
          }}
          onClose={closeReporter}
        />
      )}
    </ProductFeedbackCapabilityContext.Provider>
  );
};
